/**
 * The single source of truth for the Bearer access token (ADR-026,
 * Decision F3). `sessionStorage` only - never `localStorage`, never
 * logged, never placed in the TanStack Query cache. This is a small,
 * dependency-free leaf module deliberately kept separate from both the
 * HTTP client (lib/api/client.ts) and the auth session module
 * (features/auth/session.tsx): both of those import this module instead
 * of each other, so there is no circular dependency between "attach the
 * header" and "own the session".
 */

const STORAGE_KEY = 'mahfuz.accessToken';

let unauthorizedListeners: Array<() => void> = [];

export function getAccessToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can throw in locked-down/private-browsing contexts; treat as
    // "no session" rather than crashing the app.
    return null;
  }
}

export function setAccessToken(token: string): void {
  sessionStorage.setItem(STORAGE_KEY, token);
}

export function clearAccessToken(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/** Registers a callback fired whenever the HTTP client observes a 401. */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners = [...unauthorizedListeners, listener];
  return () => {
    unauthorizedListeners = unauthorizedListeners.filter((entry) => entry !== listener);
  };
}

/** Called only by the HTTP client, on every 401 response. */
export function notifyUnauthorized(): void {
  clearAccessToken();
  for (const listener of unauthorizedListeners) {
    listener();
  }
}
